import * as UpstreamPrefab from "@prefab-cloud/prefab-cloud-react";
import { isNotNil } from "@vendrinc/frontend-ts";
import * as React from "react";
import * as Prefab from "~/domain/prefab";
import { useAnalytics } from "~/hooks/useAnalytics";
import { useTrackedExperiments } from "~/hooks/useTrackedExperiments";

export const usePrefab = () => {
  const prefab = UpstreamPrefab.usePrefab();
  const { isEnabled: isEnabledUpstream, get: getUpstream } = prefab;

  const { has: hasTrackedExperiment, add: addTrackedExperiment } =
    useTrackedExperiments();

  const { track } = useAnalytics();

  const trackExperimentIfNecessary = React.useCallback(
    (key: Prefab.FlagName, value: unknown) => {
      if (
        Prefab.isKnownExperiment(key) &&
        isNotNil(value) &&
        !hasTrackedExperiment(key, value)
      ) {
        addTrackedExperiment(key, value);

        track({
          type: "$experiment_started",
          properties: {
            "Experiment name": key,
            "Variant name": value?.toString() ?? "not-set",
          },
        });
      }
    },
    [addTrackedExperiment, hasTrackedExperiment, track],
  );

  // TODO: Remove me after 7/16/2025
  const publicApiTestingOverride = (key: Prefab.FlagName) => {
    return (key === "clerk-auth" || key === "seller-portal") &&
      typeof window !== "undefined"
      ? document.cookie
          .split("; ")
          .find((row) => row.startsWith(`clerk_auth_override=`))
          ?.split("=")[1] === "true"
      : false;
  };

  const isEnabled = React.useCallback(
    (key: Prefab.FlagName) => {
      const value = isEnabledUpstream(key) || publicApiTestingOverride(key);
      trackExperimentIfNecessary(key, value);
      return value;
    },
    [isEnabledUpstream, trackExperimentIfNecessary],
  );

  const get = React.useCallback(
    <T extends Prefab.FlagName>(key: T): Prefab.Flags[T] | null | undefined => {
      const value = Prefab.decodeFlag(key, getUpstream(key));
      trackExperimentIfNecessary(key, value);

      // TODO: Remove this once Clerk Auth is enabled site wide
      if (key === ("clerk-auth" as const) && typeof window !== "undefined") {
        const clerkAuthOverride = document.cookie
          .split("; ")
          .find((row) => row.startsWith(`clerk_auth_override=`))
          ?.split("=")[1];

        if (clerkAuthOverride) {
          return {
            "clerk-auth": clerkAuthOverride === "true",
          } as unknown as Prefab.Flags[T];
        }

        return value;
      }

      return value;
    },
    [getUpstream, trackExperimentIfNecessary],
  );

  return {
    ...prefab,
    isEnabled,
    get,
  };
};
